/**
 * LeaveProtectionProvider
 *
 * Provides a global, accurate “don’t leave/refresh” guard for high-stakes flows.
 *
 * - Blocks in-app navigation (custom history-based blocker, compatible with BrowserRouter)
 * - Warns on tab close/refresh (beforeunload)
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { ConfirmationDialog } from '../ui/dialogs/ConfirmationDialog';

/** Returns full URL path for comparison (pathname + search + hash). */
function getFullPath(): string {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
}

/** Parses a URL-like value into a full path string. */
function toFullPath(url: string | URL | null | undefined): string {
  if (!url) return getFullPath();
  try {
    const u = typeof url === 'string' ? new URL(url, window.location.origin) : url;
    return `${u.pathname}${u.search}${u.hash}`;
  } catch {
    return typeof url === 'string' ? url : getFullPath();
  }
}

interface BlockerResult {
  readonly state: 'blocked' | 'idle';
  readonly proceed?: () => void;
  readonly reset?: () => void;
}

/**
 * Custom navigation blocker compatible with BrowserRouter.
 * Patches history.pushState/replaceState and intercepts popstate.
 */
function useNavigationBlocker(shouldBlock: boolean): BlockerResult {
  const [state, setState] = useState<'blocked' | 'idle'>('idle');
  const pendingActionRef = useRef<(() => void) | null>(null);
  const restoringRef = useRef(false);

  const proceed = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setState('idle');
    action?.();
  }, []);

  const reset = useCallback(() => {
    pendingActionRef.current = null;
    setState('idle');
  }, []);

  useEffect(() => {
    if (!shouldBlock) return;

    const originalPushState = history.pushState.bind(history);
    const originalReplaceState = history.replaceState.bind(history);

    const tryBlock = (nextUrl: string, execute: () => void): boolean => {
      const current = getFullPath();
      if (nextUrl === current) return false;
      if (pendingActionRef.current) return false;
      pendingActionRef.current = execute;
      setState('blocked');
      return true;
    };

    const patchedPushState = (
      stateData: unknown,
      title: string,
      url?: string | URL | null
    ): void => {
      const next = toFullPath(url ?? undefined);
      if (tryBlock(next, () => originalPushState(stateData, title, url))) return;
      originalPushState(stateData, title, url);
    };

    const patchedReplaceState = (
      stateData: unknown,
      title: string,
      url?: string | URL | null
    ): void => {
      const next = toFullPath(url ?? undefined);
      if (tryBlock(next, () => originalReplaceState(stateData, title, url)))
        return;
      originalReplaceState(stateData, title, url);
    };

    const handlePopState = (): void => {
      if (restoringRef.current) {
        restoringRef.current = false;
        return;
      }
      if (!pendingActionRef.current) {
        pendingActionRef.current = (): void => {
          history.go(-1);
        };
        setState('blocked');
      }
      restoringRef.current = true;
      history.go(1);
    };

    history.pushState = patchedPushState;
    history.replaceState = patchedReplaceState;
    window.addEventListener('popstate', handlePopState);

    return (): void => {
      history.pushState = originalPushState;
      history.replaceState = originalReplaceState;
      window.removeEventListener('popstate', handlePopState);
      pendingActionRef.current = null;
      setState('idle');
    };
  }, [shouldBlock]);

  return { state, proceed, reset };
}

interface LeaveProtectionCopy {
  readonly title: string;
  readonly body: string;
  readonly stayLabel: string;
  readonly leaveLabel: string;
}

interface LeaveProtectionContextValue {
  readonly isEnabled: boolean;
  readonly enable: (copy?: Partial<LeaveProtectionCopy>) => void;
  readonly disable: () => void;
}

const DEFAULT_COPY: LeaveProtectionCopy = {
  title: "Don't leave yet",
  body: 'If you leave or refresh, you may not finish verification right now. If this was your last attempt this month, you might not be able to start again.',
  stayLabel: 'Stay here',
  leaveLabel: 'Leave anyway'
};

const LeaveProtectionContext =
  createContext<LeaveProtectionContextValue | null>(null);

// eslint-disable-next-line react-refresh/only-export-components -- Hook is the public API for this provider
export function useLeaveProtection(): LeaveProtectionContextValue {
  const ctx = useContext(LeaveProtectionContext);
  if (!ctx) {
    throw new Error(
      'useLeaveProtection must be used within LeaveProtectionProvider'
    );
  }
  return ctx;
}

export const LeaveProtectionProvider: React.FC<{
  readonly children: React.ReactNode;
}> = ({ children }) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [copy, setCopy] = useState<LeaveProtectionCopy>(DEFAULT_COPY);

  const enable = useCallback((next?: Partial<LeaveProtectionCopy>): void => {
    setCopy({ ...DEFAULT_COPY, ...(next || {}) });
    setIsEnabled(true);
  }, []);

  const disable = useCallback((): void => {
    setIsEnabled(false);
    setCopy(DEFAULT_COPY);
  }, []);

  const blocker = useNavigationBlocker(isEnabled);

  React.useEffect(() => {
    if (!isEnabled) return;
    const handler = (e: BeforeUnloadEvent): void => {
      e.preventDefault();
      // Required for Chrome to show a confirmation prompt.
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return (): void => {
      window.removeEventListener('beforeunload', handler);
    };
  }, [isEnabled]);

  const value = useMemo<LeaveProtectionContextValue>(
    () => ({ isEnabled, enable, disable }),
    [disable, enable, isEnabled]
  );

  return (
    <LeaveProtectionContext.Provider value={value}>
      {children}
      <ConfirmationDialog
        isOpen={blocker.state === 'blocked'}
        tone="warning"
        title={copy.title}
        description={copy.body}
        confirmLabel={copy.leaveLabel}
        cancelLabel={copy.stayLabel}
        onConfirm={() => {
          blocker.proceed?.();
        }}
        onClose={() => {
          blocker.reset?.();
        }}
      />
    </LeaveProtectionContext.Provider>
  );
};

export default LeaveProtectionProvider;
